import React from "react";
import { Link } from "react-router-dom";
import logo4 from "../assets/logo/logo4.png";
import logo5 from "../assets/logo/logo5.png";
import abrLogo from "../assets/logo/abr.png";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

const Contact = () => {
  return (
    <>
      {/* Contact Info - Styled like the FAQ section in the design */}
      <section className="py-24 lg:py-32 bg-gray-200 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          
          {/* Left Title */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ink-900 font-display mb-6">
              Get In Touch
            </h2>
            <p className="text-ink-600 text-lg mb-8 leading-relaxed font-light">
              340 Real Estate Company Property, Sales, and Management on St John US Virgin Islands
            </p>
            <Link 
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-brand-dark text-white text-sm font-bold rounded-full hover:bg-brand-dark/95 hover:scale-105 transition-all duration-300 mb-8 shadow-md"
            >
              Send Us a Message <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="text-sm font-semibold tracking-widest uppercase text-ink-500 font-display">
              Licensed USVI Broker
            </p>
          </motion.div>

          {/* Right List (FAQ style) */}
          <div className="lg:col-span-7 flex flex-col">
            
            {/* Item 1 */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="border-b border-gray-300 py-6"
            >
              <h3 className="text-xl font-semibold text-ink-900 font-display mb-3">Office Address</h3>
              <a 
                href="https://maps.google.com/?q=340+Real+Estate+Company,+St.+John,+VI"
                target="_blank"
                rel="noopener noreferrer"
                className="text-ink-600 hover:text-tropical-600 transition-colors leading-relaxed font-light block"
              >
                <strong>340 Real Estate Company</strong><br />
                PO Box 766<br />
                St John, VI 00831<br />
                United States Virgin Islands
              </a>
            </motion.div>

            {/* Item 2 */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="border-b border-gray-300 py-6"
            >
              <h3 className="text-xl font-semibold text-ink-900 font-display mb-3">Email Address</h3>
              <a 
                href="mailto:340realestateco@gmail.com"
                className="text-ink-600 hover:text-tropical-600 transition-colors text-lg"
              >
                340realestateco@gmail.com
              </a>
              <p className="text-sm mt-2 text-ink-400 font-light">We'll respond within 24 hours</p>
            </motion.div>

            {/* Item 3 */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="border-b border-gray-300 py-6"
            >
              <h3 className="text-xl font-semibold text-ink-900 font-display mb-3">Phone Number</h3>
              <div className="flex flex-col gap-2">
                <a 
                  href="tel:+13406436068"
                  className="text-ink-600 hover:text-tropical-600 transition-colors text-lg"
                >
                  +1 340-643-6068
                </a>
                <a 
                  href="tel:+13407794478"
                  className="text-ink-600 hover:text-tropical-600 transition-colors text-lg"
                >
                  +1 340-779-4478
                </a>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* CTA Section - Light Background */}
      <section className="py-24 lg:py-32 bg-gray-100 px-6 lg:px-12 border-t border-gray-100">
        <div className="max-w-6xl mx-auto text-center">
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-sm font-semibold tracking-widest uppercase text-tropical-500 mb-6 font-display">
              Trusted Credentials & Affiliations
            </p>
            
            {/* Logos & Labels */}
            <div className="flex flex-wrap justify-center items-end gap-12 lg:gap-20 mb-16">
              <div className="flex flex-col items-center gap-3">
                <img src={logo4} alt="Jolly Dog" className="h-16 sm:h-20 object-contain mix-blend-multiply" />
                <span className="text-xs font-semibold text-ink-500 uppercase tracking-widest">Jolly Dog</span>
              </div>
              <div className="flex flex-col items-center gap-3">
                <img src={logo5} alt="Zemi Logo" className="h-16 sm:h-20 object-contain mix-blend-multiply" />
                <span className="text-xs font-semibold text-ink-500 uppercase tracking-widest">Zemi</span>
              </div>
              <div className="flex flex-col items-center gap-3">
                <div className="h-16 sm:h-20 flex items-center justify-center">
                  <span className="font-bold text-ink-800 text-lg sm:text-xl font-display text-center leading-tight">Friends of Virgin Islands<br/>National Park</span>
                </div>
                <span className="text-xs font-semibold text-ink-500 uppercase tracking-widest">National Park</span>
              </div>
              <div className="flex flex-col items-center gap-3">
                <img src={abrLogo} alt="ABR" className="h-16 sm:h-20 object-contain mix-blend-multiply" />
                <span className="text-xs font-semibold text-ink-500 uppercase tracking-widest">ABR® Certified</span>
              </div>
            </div>

            {/* Quick Stats Banner */}
            <div className="flex flex-wrap justify-center items-center gap-x-8 gap-y-4 mb-20 text-sm font-medium text-ink-600 bg-white rounded-full py-4 px-8 inline-flex border border-gray-100 shadow-xl">
              <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-tropical-500"></span> 1978 Est. on St. John</span>
              <span className="hidden sm:block text-gray-300">|</span>
              <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-tropical-500"></span> 48 Years of Local Expertise</span>
              <span className="hidden sm:block text-gray-300">|</span>
              <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-tropical-500"></span> USVI Licensed Broker</span>
              <span className="hidden sm:block text-gray-300">|</span>
              <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-tropical-500"></span> St. John Island Experts</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-ink-900 rounded-[3rem] p-12 sm:p-20 relative overflow-hidden"
          >
            {/* Abstract gradient in CTA */}
            <div className="absolute inset-0 opacity-20 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-tropical-500 via-transparent to-transparent"></div>
            
            <div className="relative z-10">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white font-display mb-10 leading-tight">
                Ready to find your dream <br/> property on St. John?
              </h2>
              
              <Link 
                to="/properties"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-ink-900 text-sm font-bold rounded-full hover:bg-gray-100 transition-transform hover:scale-105 duration-300"
              >
                Start Your Search Today <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Contact;
